import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowClockwise, Plus } from 'shared/icons';
import PageHeader from '../components/layout/PageHeader';
import NavioCard from '../components/navios/NavioCard';
import NavioFormModal from '../components/navios/NavioFormModal';
import NavioPosicaoModal from '../components/navios/NavioPosicaoModal';
import NaviosMap from '../components/navios/NaviosMap';
import Button from '../components/ui/Button';
import ConfirmModal from '../components/ui/ConfirmModal';
import ErrorCard from '../components/ui/ErrorCard';
import FilterChip from '../components/ui/FilterChip';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { VESSEL_COLORS } from '../constants/navios';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import api from '../services/api';
import { FormatDurationHuman } from '../utils/format';

// ─────────────────────────────────────────────────────────────────────────────
// NAVIOS — os navios que trazem as importações, Ásia → Santos.
//
// Mobile primeiro: o mapa em cima (pinça para zoom), a lista em cards embaixo.
// A tela carrega TODOS os navios ativos de uma vez (com a trilha) e filtra por
// tag aqui mesmo: são poucos navios, e assim a cor de cada um não muda quando
// o filtro muda. Tocar no navio do mapa destaca o card dele.
// ─────────────────────────────────────────────────────────────────────────────

const TICK_MS = 60_000;

const Navios = () => {
    const toast = useToast();
    const { checkPermission } = useAuth();
    const canWrite = checkPermission('navios:write');
    const { effectiveTheme } = useTheme();
    const [searchParams, setSearchParams] = useSearchParams();
    const tag = searchParams.get('tag') || null;

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [now, setNow] = useState(() => Date.now());

    // undefined = fechado; null = novo; objeto = editar.
    const [formVessel, setFormVessel] = useState(undefined);
    const [formErrors, setFormErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [positionVessel, setPositionVessel] = useState(null);
    const [archiveVessel, setArchiveVessel] = useState(null);

    const cardRefs = useRef(new Map());

    const load = useCallback(async () => {
        try {
            const result = await api.getNavios({ trilha: 'true' });
            setData(result);
            setError(null);
        } catch (err) {
            setError(err?.message || 'Erro ao carregar os navios');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), TICK_MS);
        return () => clearInterval(timer);
    }, []);

    const items = useMemo(() => data?.items || [], [data]);
    const tags = useMemo(() => data?.tags || [], [data]);

    // Cor fixa por navio: ordem de cadastro, independente do filtro.
    const colorOf = useMemo(() => {
        const palette = effectiveTheme === 'light' ? VESSEL_COLORS.light : VESSEL_COLORS.dark;
        const order = [...items].sort(
            (a, b) => String(a.created_at).localeCompare(String(b.created_at)) || a.imo.localeCompare(b.imo),
        );
        const indexById = new Map(order.map((item, index) => [item.id, index]));
        return (id) => palette[(indexById.get(id) ?? 0) % palette.length];
    }, [items, effectiveTheme]);

    const visible = useMemo(() => {
        if (!tag) {
            return items;
        }
        const key = tag.toLowerCase();
        return items.filter((item) => item.tags.some((itemTag) => itemTag.toLowerCase() === key));
    }, [items, tag]);

    const tagCounts = useMemo(() => {
        const counts = new Map();
        for (const item of items) {
            for (const itemTag of item.tags) {
                counts.set(itemTag.toLowerCase(), (counts.get(itemTag.toLowerCase()) || 0) + 1);
            }
        }
        return counts;
    }, [items]);

    const setTag = (value) => {
        const next = new URLSearchParams(searchParams);
        if (value) {
            next.set('tag', value);
        } else {
            next.delete('tag');
        }
        setSearchParams(next, { replace: true });
    };

    const select = useCallback((id) => {
        setSelectedId((current) => (current === id ? null : id));
        if (id) {
            cardRefs.current.get(id)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, []);

    const syncNow = async () => {
        setSyncing(true);
        try {
            const result = await api.syncNavios();
            if (result?.reason === 'sem_provider') {
                toast.info(result.message);
            } else {
                const saved = (result?.results || []).filter((entry) => entry.outcome === 'gravada').length;
                toast.success(`${saved} ${saved === 1 ? 'posição nova' : 'posições novas'} do ${result.provider}`);
            }
            await load();
        } catch (err) {
            toast.error(err?.message || 'Erro ao atualizar');
        } finally {
            setSyncing(false);
        }
    };

    const saveVessel = async (body) => {
        setSaving(true);
        setFormErrors({});
        try {
            if (formVessel) {
                await api.updateNavio(formVessel.id, body);
                toast.success('Navio atualizado');
            } else {
                const created = await api.createNavio(body);
                toast.success(`${created.name} cadastrado`);
                setSelectedId(created.id);
            }
            setFormVessel(undefined);
            await load();
        } catch (err) {
            const fieldDetail = Array.isArray(err?.details)
                ? err.details.find((detail) => detail.path === 'imo' || detail.path === 'mmsi')
                : null;
            if (fieldDetail?.path === 'mmsi') {
                setFormErrors({ mmsi: err.message });
            } else if (fieldDetail || err?.code === 'VESSEL_ALREADY_TRACKED') {
                setFormErrors({ imo: err.message });
            } else {
                toast.error(err?.message || 'Erro ao salvar o navio');
            }
        } finally {
            setSaving(false);
        }
    };

    const savePosition = async (body) => {
        setSaving(true);
        try {
            const result = await api.addNavioPosicao(positionVessel.id, body);
            if (result.accepted) {
                toast.success('Posição registrada');
                setPositionVessel(null);
                await load();
            } else {
                toast.info(result.message || 'Leitura descartada');
            }
        } catch (err) {
            toast.error(err?.message || 'Erro ao registrar a posição');
        } finally {
            setSaving(false);
        }
    };

    const confirmArchive = async () => {
        setSaving(true);
        try {
            await api.updateNavio(archiveVessel.id, { active: false });
            toast.success(`${archiveVessel.name} arquivado`);
            setArchiveVessel(null);
            if (selectedId === archiveVessel.id) {
                setSelectedId(null);
            }
            await load();
        } catch (err) {
            toast.error(err?.message || 'Erro ao arquivar');
        } finally {
            setSaving(false);
        }
    };

    const lastUpdate = data?.ultima_atualizacao ? (now - new Date(data.ultima_atualizacao).getTime()) / 60_000 : null;

    return (
        <div className="space-y-4">
            <PageHeader
                title="Navios"
                subtitle="Importações a caminho de Santos"
                actions={
                    canWrite ? (
                        <>
                            <Button variant="secondary" size="sm" onClick={syncNow} loading={syncing}>
                                <span className="inline-flex items-center gap-1.5">
                                    <ArrowClockwise size={16} /> Atualizar agora
                                </span>
                            </Button>
                            <Button size="sm" onClick={() => setFormVessel(null)}>
                                <span className="inline-flex items-center gap-1.5">
                                    <Plus size={16} /> Navio
                                </span>
                            </Button>
                        </>
                    ) : null
                }
            />

            <p className="text-xs text-mono-500 dark:text-mono-400">
                {lastUpdate === null ? 'Sem leituras ainda' : `Atualizado há ${FormatDurationHuman(lastUpdate)}`}
                {' · '}
                {data?.provider?.configured
                    ? `posição AIS: ${data.provider.name}, às 0h, 6h, 12h e 18h`
                    : 'sem provider AIS — posições pelo Claude (MCP) ou à mão'}
            </p>

            {tags.length > 0 ? (
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
                    <FilterChip active={!tag} count={items.length} onClick={() => setTag(null)}>
                        Todos
                    </FilterChip>
                    {tags.map((itemTag) => (
                        <FilterChip
                            key={itemTag}
                            active={tag?.toLowerCase() === itemTag.toLowerCase()}
                            count={tagCounts.get(itemTag.toLowerCase()) || 0}
                            onClick={() => setTag(tag?.toLowerCase() === itemTag.toLowerCase() ? null : itemTag)}
                        >
                            <span className="whitespace-nowrap">{itemTag}</span>
                        </FilterChip>
                    ))}
                </div>
            ) : null}

            {error ? <ErrorCard message={error} action="Tentar de novo" actionFn={load} /> : null}

            {loading ? (
                <Skeleton className="h-64 w-full rounded-xl" />
            ) : (
                <NaviosMap vessels={visible} colorOf={colorOf} selectedId={selectedId} onSelect={select} />
            )}

            {!loading && items.length === 0 && !error ? (
                <div className="rounded-xl border border-dashed border-mono-300 p-8 text-center text-sm text-mono-600 dark:border-mono-700 dark:text-mono-400">
                    Nenhum navio acompanhado ainda.
                    {canWrite ? ' Cadastre pelo IMO em “+ Navio”.' : ''}
                </div>
            ) : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                {visible.map((vessel) => (
                    <NavioCard
                        key={vessel.id}
                        ref={(element) => {
                            if (element) {
                                cardRefs.current.set(vessel.id, element);
                            } else {
                                cardRefs.current.delete(vessel.id);
                            }
                        }}
                        vessel={vessel}
                        color={colorOf(vessel.id)}
                        selected={selectedId === vessel.id}
                        canWrite={canWrite}
                        now={now}
                        onSelect={select}
                        onEdit={(target) => {
                            setFormErrors({});
                            setFormVessel(target);
                        }}
                        onPosition={setPositionVessel}
                        onArchive={setArchiveVessel}
                    />
                ))}
            </div>

            <NavioFormModal
                isOpen={formVessel !== undefined}
                vessel={formVessel || null}
                knownTags={tags}
                saving={saving}
                errors={formErrors}
                onClose={() => setFormVessel(undefined)}
                onSubmit={saveVessel}
            />
            <NavioPosicaoModal
                isOpen={Boolean(positionVessel)}
                vessel={positionVessel}
                saving={saving}
                onClose={() => setPositionVessel(null)}
                onSubmit={savePosition}
            />
            <ConfirmModal
                isOpen={Boolean(archiveVessel)}
                onClose={() => setArchiveVessel(null)}
                onConfirm={confirmArchive}
                loading={saving}
                title="Arquivar navio"
                message={`${archiveVessel?.name || ''} sai do mapa, da lista e da atualização automática. Nada é apagado: cadastrar o mesmo IMO de novo reativa.`}
                confirmText="Arquivar"
            />
        </div>
    );
};

export default Navios;
